import useWindowSize from '@hooks/use-window-size';
import { useCallback, useEffect, useRef } from 'react';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { Product } from '@interfaces/models/product';

export interface ImpressionProductDetails {
  product_id: string;
  product_brand: string;
  product_unitprice_ati: string;
  product_card_tag: string;
  currency: string;
  seller_id: string;
  product_universe: string;
  product_feed_category: string;
  product_position: string;
}

export const getRowCount = (isWiderThanLg: boolean, isWiderThanMd: boolean) => {
  if (isWiderThanLg) {
    return 4;
  } else if (isWiderThanMd) {
    return 3;
  }
  return 2;
};

export const getProductTrackingData = (
  product: Product,
): Omit<ImpressionProductDetails, 'product_feed_category' | 'product_position'> => ({
  product_id: String(product.id),
  product_brand: product.brand?.name,
  product_unitprice_ati: String((product.price?.cents ?? 0) / 100 ?? ''),
  product_card_tag: product.tags?.join(',') ?? '',
  currency: product.price?.currency ?? '',
  seller_id: product.seller?.id ? product.seller?.id : '',
  product_universe: 'universeId' in product ? String(product.universeId) : '',
});

const NUMBER_OF_PRODUCTS_PER_EVENT = 12;

const useSimilarProductsCatalogTracking = (productFeedCategory?: string) => {
  const { isWiderThanLg } = useWindowSize();
  const { isWiderThanMd } = useWindowSize();
  const rowCountRef = useRef(getRowCount(isWiderThanLg, isWiderThanMd));

  useEffect(() => {
    rowCountRef.current = getRowCount(isWiderThanLg, isWiderThanMd);
  }, [isWiderThanLg, isWiderThanMd]);

  const seenProductsDataRef = useRef([]);

  const { sendAnalyticEvent } = useAnalyticEvents('product');

  // This useEffect is used to send impression event for any remaining products when user leaves the page
  useEffect(() => {
    const sendLastProductImpressionsEvent = () => {
      if (seenProductsDataRef.current.length > 0) {
        sendAnalyticEvent('product_impression', { product_card_details: seenProductsDataRef.current });
      }
    };
    // If users exits page without component unmounting (e.g. by clicking in link header)
    // we send a tracking event for all remaining products
    window.addEventListener('beforeunload', sendLastProductImpressionsEvent);

    return () => {
      window.removeEventListener('beforeunload', sendLastProductImpressionsEvent);
      // If users exits page and component unmounts ( i.e. via next routing, for example when clicking on product card )
      // we send a tracking event for all remaining products
      sendLastProductImpressionsEvent();
    };
  }, []);

  // this fn is used to send product impressions events on scroll
  const sendProductImpressionsEvent = useCallback(() => {
    if (seenProductsDataRef.current.length === 0) {
      return;
    }
    const productData = seenProductsDataRef.current;
    // resetting the seen products
    seenProductsDataRef.current = [];
    const sendTrackingEvents = () => sendAnalyticEvent('product_impression', { product_card_details: productData });
    // using requestIdleCallback to avoid blocking the main thread
    window.requestIdleCallback(sendTrackingEvents);
  }, []);

  const onRowReveal = useCallback(
    (index: number, productsOnRow: Product[]) => {
      const newProductData = productsOnRow.map((product, i) => ({
        ...getProductTrackingData(product),
        product_feed_category: productFeedCategory,
        product_position: String(index + i),
      }));
      seenProductsDataRef.current = [...seenProductsDataRef.current, ...newProductData];
      if (seenProductsDataRef.current.length === NUMBER_OF_PRODUCTS_PER_EVENT) {
        sendProductImpressionsEvent();
      }
    },
    [sendProductImpressionsEvent],
  );

  return { onRowReveal, rowCountRef };
};

export default useSimilarProductsCatalogTracking;
